import { useCallback, useState } from 'react';
import { SubscriptionProductItem } from '../utils/subscriptionDetail/types';

type DiffType = 'create' | 'update' | 'remove';
type PickSubscriptionProduct = Pick<
  SubscriptionProductItem,
  'currentPriceAmount' | 'lineId' | 'sellingPlanId' | 'weight' | 'productId' | 'variantId'
>;
export type Product = PickSubscriptionProduct & {
  originQuantity: number;
  newQuantity: number;
};

type DiffItem = {
  type: DiffType;
  product: Product;
};

export type QuantityUpdate = { product: SubscriptionProductItem; newQty: number };

export type DiffState = Map<string, DiffItem>;

export const useProductCart = () => {
  const [diff, setDiff] = useState<DiffState>(new Map());

  const changeQuantity = useCallback((updates: QuantityUpdate | QuantityUpdate[]) => {
    const list = Array.isArray(updates) ? updates : [updates];
    setDiff((prev) => {
      const next = new Map(prev);

      for (const { product, newQty } of list) {
        const type = resolveType(product.quantity, newQty);
        if (!type) {
          next.delete(product.id);
        } else {
          next.set(product.id, {
            type,
            product: {
              productId: product.productId,
              variantId: product.variantId,
              weight: product.weight,
              currentPriceAmount: product.currentPriceAmount,
              sellingPlanId: product.sellingPlanId,
              lineId: product.lineId,
              originQuantity: product.quantity,
              newQuantity: newQty,
            },
          });
        }
      }

      return next;
    });
  }, []);

  const resetDiff = useCallback(() => {
    setDiff((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const reCalcDiff = useCallback((updates: Record<string, PickSubscriptionProduct>) => {
    setDiff((prev) => {
      if (prev.size === 0) return prev;

      const next = new Map(prev);

      next.forEach((item, key) => {
        const update = updates[key];
        if (!update) return;

        next.set(key, {
          type: item.type,
          product: {
            ...item.product,
            currentPriceAmount: update.currentPriceAmount,
            lineId: update.lineId,
            sellingPlanId: update.sellingPlanId,
          },
        });
      });

      return next;
    });
  }, []);

  return {
    diff,
    changeQuantity,
    resetDiff,
    reCalcDiff,
  };
};

const resolveType = (origin: number, next: number): DiffType | null => {
  if (origin === next) return null;
  if (origin === 0) return 'create';
  if (next === 0) return 'remove';
  return 'update';
};
